/**
 * Minimal ZIP writer, store-only (no compression).
 *
 * A GoBD/Z3 archive has to be readable "ohne Installation von Fremdsoftware
 * auf den Rechnern der Finanzbehörde", and stored entries are the most
 * conservative thing a ZIP can contain — every extractor since 1989 handles
 * them. The files are CSV and XML of modest size, so the space saved by
 * deflating is not worth pulling in a compression library and the extra
 * failure modes that come with it.
 */

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let value = i;
    for (let bit = 0; bit < 8; bit += 1) {
      value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
    }
    table[i] = value >>> 0;
  }
  return table;
})();

export function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    crc = CRC_TABLE[(crc ^ bytes[i]!) & 0xff]! ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

export interface ZipEntry {
  /** Forward-slash path inside the archive. */
  name: string;
  data: Uint8Array | string;
  /** Defaults to now. */
  date?: Date;
}

/** MS-DOS packed date and time, which is what the ZIP header carries. */
function dosDateTime(date: Date): { time: number; date: number } {
  const time =
    (date.getHours() << 11) | (date.getMinutes() << 5) | Math.floor(date.getSeconds() / 2);
  const packedDate =
    ((date.getFullYear() - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate();
  return { time, date: packedDate };
}

class ByteWriter {
  private chunks: Uint8Array[] = [];
  private length = 0;

  push(bytes: Uint8Array): void {
    this.chunks.push(bytes);
    this.length += bytes.length;
  }

  u16(value: number): void {
    const bytes = new Uint8Array(2);
    new DataView(bytes.buffer).setUint16(0, value, true);
    this.push(bytes);
  }

  u32(value: number): void {
    const bytes = new Uint8Array(4);
    new DataView(bytes.buffer).setUint32(0, value >>> 0, true);
    this.push(bytes);
  }

  get offset(): number {
    return this.length;
  }

  toBlob(mime: string): Blob {
    return new Blob(this.chunks as BlobPart[], { type: mime });
  }
}

const encoder = new TextEncoder();

export function createZip(entries: readonly ZipEntry[]): Blob {
  const writer = new ByteWriter();
  const central: Array<{
    nameBytes: Uint8Array;
    crc: number;
    size: number;
    offset: number;
    time: number;
    date: number;
  }> = [];

  for (const entry of entries) {
    const nameBytes = encoder.encode(entry.name);
    const data = typeof entry.data === 'string' ? encoder.encode(entry.data) : entry.data;
    const crc = crc32(data);
    const { time, date } = dosDateTime(entry.date ?? new Date());
    const offset = writer.offset;

    writer.u32(0x04034b50);
    writer.u16(20); // version needed
    // Bit 11 marks the filename as UTF-8, so umlauts in a file name survive.
    writer.u16(0x0800);
    writer.u16(0); // stored
    writer.u16(time);
    writer.u16(date);
    writer.u32(crc);
    writer.u32(data.length);
    writer.u32(data.length);
    writer.u16(nameBytes.length);
    writer.u16(0); // extra field length
    writer.push(nameBytes);
    writer.push(data);

    central.push({ nameBytes, crc, size: data.length, offset, time, date });
  }

  const centralStart = writer.offset;

  for (const item of central) {
    writer.u32(0x02014b50);
    writer.u16(20); // version made by
    writer.u16(20); // version needed
    writer.u16(0x0800);
    writer.u16(0);
    writer.u16(item.time);
    writer.u16(item.date);
    writer.u32(item.crc);
    writer.u32(item.size);
    writer.u32(item.size);
    writer.u16(item.nameBytes.length);
    writer.u16(0); // extra
    writer.u16(0); // comment
    writer.u16(0); // disk number
    writer.u16(0); // internal attributes
    writer.u32(0); // external attributes
    writer.u32(item.offset);
    writer.push(item.nameBytes);
  }

  const centralSize = writer.offset - centralStart;

  writer.u32(0x06054b50);
  writer.u16(0);
  writer.u16(0);
  writer.u16(central.length);
  writer.u16(central.length);
  writer.u32(centralSize);
  writer.u32(centralStart);
  writer.u16(0);

  return writer.toBlob('application/zip');
}

/** Trigger a browser download. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  // Give Safari a moment to start the download before the URL is revoked.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
